/**
 * The combat layer's authoring rules — D234 (`docs/design/actors.md` §6–§7;
 * Mike's rulings 3 and 4 of 2026-10-04; Calder's `actors-content.md` §5.4).
 *
 * @remarks
 * Every rule here is the D31/D47 dead-content law or the reference walk,
 * applied to a fight:
 *
 * - **A foe needs a body to strike, and a body needs a foe.** `combat` on
 *   any actor requires `player` on the manifest — a strike against a player
 *   with no health does nothing, so every attack line would be a lie about
 *   danger. And `player` with no foe anywhere is a health nothing can lower
 *   and a death ending nothing can select.
 * - **Every death ending exists.** The manifest's `player.deathEndingId` and
 *   each foe's `combat.deathEndingId` name declared endings; the reachability
 *   rule counts both as selectors, exactly as a `set_ending` is (a death IS a
 *   `set_ending`, in the engine's own words).
 * - **Every line a fight speaks is walked** — `attackNarratives`,
 *   `hurtNarratives`, `defeatNarratives`, the `defeatedDescription`'s
 *   readings — and `hostileWhen` is walked like any condition; its
 *   producibility is the D184 walk's, and that walk is what decides ruling
 *   3 ("attackable only where hostility can become true"): a `hostileWhen`
 *   the pack can never make true is a refused pack, so every foe that loads
 *   is a foe that can be fought. `onDefeat` is an effect home
 *   (`effect-homes.ts`) and is walked with every other.
 * - **`verbNarratives.attack` on a foe** is refused by the schema itself:
 *   the strike lands, so the line could never speak.
 *
 * `weapon` and `armor` need nothing here: a weapon is a number, and armor's
 * one rule (worn or nothing) is unrepresentable in the schema.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { brokenReference, requireDeclared } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkCondition, checkNarrativeLines, checkNarrativeText, declaredIdSetsOf } from './reference-walkers.js';

/**
 * Every ending a DEATH selects — the manifest's, and each foe's own — the
 * selectors the ending-reachability rule counts beside `set_ending`.
 *
 * @param contentPack - The parsed pack
 * @returns The death ending ids, manifest first, then foes in declared order
 */
export const deathEndingIdsOf = (contentPack: ContentPackDefinition): readonly string[] => [
	...(contentPack.manifest.player === undefined ? [] : [contentPack.manifest.player.deathEndingId as string]),
	...contentPack.actors.flatMap((actor) => (actor.combat?.deathEndingId === undefined ? [] : [actor.combat.deathEndingId as string])),
];

/**
 * Validate the fight's declarations against the pack — see the module
 * remarks.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateCombatAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const { player } = contentPack.manifest;

	if (player !== undefined) {
		requireDeclared(declared.endingIds, player.deathEndingId, 'manifest.player.deathEndingId', 'the player\'s death selects an undeclared ending', failures);

		if (!contentPack.actors.some((actor) => actor.combat !== undefined)) {
			failures.push(
				brokenReference(
					'manifest.player',
					String(player.health),
					'the manifest declares the player\'s health, and no actor declares `combat` — a health nothing can lower, and a death ending nothing can select',
				),
			);
		}
	}

	contentPack.actors.forEach((actor, actorIndex) => {
		const { combat } = actor;

		if (combat === undefined) {
			return;
		}

		const combatPath = `actors[${String(actorIndex)}].combat`;

		if (player === undefined) {
			failures.push(
				brokenReference(
					combatPath,
					actor.actorId,
					'this actor is a foe, and the manifest declares no `player` — a strike against a player with no health does nothing, so every attack line would lie about danger; declare `player.health` and `player.deathEndingId`',
				),
			);
		}

		if (combat.deathEndingId !== undefined) {
			requireDeclared(declared.endingIds, combat.deathEndingId, `${combatPath}.deathEndingId`, 'this foe\'s kill selects an undeclared ending', failures);
		}

		checkCondition(combat.hostileWhen, `${combatPath}.hostileWhen`, declared, failures);
		checkNarrativeLines(combat.attackNarratives, `${combatPath}.attackNarratives`, declared, failures);
		checkNarrativeLines(combat.hurtNarratives, `${combatPath}.hurtNarratives`, declared, failures);
		checkNarrativeLines(combat.defeatNarratives, `${combatPath}.defeatNarratives`, declared, failures);

		if (combat.defeatedDescription !== undefined) {
			checkNarrativeText(combat.defeatedDescription, `${combatPath}.defeatedDescription`, declared, failures);
		}
	});

	return failures;
};
